import { forwardRef, type ReactNode, useEffect, useImperativeHandle, useRef, useState } from "react";
import classNames from "classnames";
import { Card as LCCard } from "../../Configurators/Linear/components/Card";
import { Popper, type PopperPlacementType } from "@mui/material";

type PopperTooltipProps = {
  tooltipContent: ReactNode;
  placement?: PopperPlacementType;
  zIndex?: number;
  innerClassName?: string;
  isNavigation?: boolean;
  hideTooltip?: boolean;
  onTooltipClosed?: () => void;
  onTooltipOpened?: () => void;
  disableFlip?: boolean;
  contentClassName?: string;
  cardVariant?: "rounded" | "square";
  disableHover?: boolean;
  onClose?: () => void;
} & JSX.IntrinsicElements["div"];

export type PopperTooltipHandle = {
  open: () => void;
  close: () => void;
};

export const PopperTooltip = forwardRef<PopperTooltipHandle, PopperTooltipProps>((props, ref) => {
  const {
    tooltipContent,
    placement = "bottom-start",
    children,
    className,
    zIndex,
    innerClassName,
    isNavigation,
    hideTooltip = false,
    onTooltipClosed,
    onTooltipOpened,
    disableFlip,
    contentClassName,
    cardVariant = "square",
    disableHover = false,
    onClose,
    ...divProps
  } = props;

  const [hoveredElement, setHoveredElement] = useState<string[]>([]);
  const [isClicked, setIsClicked] = useState(false);
  const enableHoverRefManipulation = useRef<boolean>(true);

  const containerRef = useRef<HTMLDivElement | null>(null);

  const tooltipRef = useRef<HTMLDivElement | null>(null);

  // Expose imperative methods
  useImperativeHandle(ref, () => ({
    open: () => setIsClicked(true),
    close: () => {
      setIsClicked(false);
      setHoveredElement([]);
    },
  }));

  // ----- HOVER HANDLERS -----
  const addHover = (el: string) => () => {
    if (!enableHoverRefManipulation.current) {
      return;
    }
    if (!disableHover && !hideTooltip) {
      setHoveredElement((prev) => prev.concat(el));
    }
    // enableHoverRefManipulation.current = false;
    // window.setTimeout(() => (enableHoverRefManipulation.current = true), 25);
  };
  const removeHover = (el: string) => () => {
    if (!enableHoverRefManipulation.current) {
      return;
    }
    // enableHoverRefManipulation.current = false;
    if (!disableHover) {
      window.setTimeout(() => setHoveredElement((prev) => prev.filter((item) => item !== el)), 100);
    }
  };

  // ----- CLICK HANDLER -----
  const toggleClick = () => {
    if (hideTooltip) return;
    setIsClicked((prev) => !prev);
  };

  // ----- OUTSIDE CLICK HANDLER -----
  useEffect(() => {
    // const handleClickOutside = (event: MouseEvent) => {
    //   if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
    //     setIsClicked(false);
    //   }
    // };

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        tooltipRef.current &&
        !tooltipRef.current.contains(target)
      ) {
        setIsClicked(false);
      }
    };

    if (isClicked) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isClicked]);

  useEffect(() => {
    if (hideTooltip) {
      setHoveredElement([]);
      setIsClicked(false);
    }
  }, [hideTooltip]);

  const isOpen = !hideTooltip && (hoveredElement.length > 0 || isClicked);

  useEffect(() => {
    if (onTooltipClosed && !isOpen) {
      onTooltipClosed();
    }
    if (onTooltipOpened && isOpen) {
      onTooltipOpened();
    }
  }, [isOpen]);

  const popperStyle = zIndex ? { zIndex } : undefined;

  return (
    <>
      <div
        ref={containerRef}
        className={classNames("scha__tooltip__container", className)}
        onMouseEnter={addHover("container")}
        onMouseLeave={removeHover("container")}
        onClick={toggleClick}
        {...divProps}
      >
        {children}
      </div>
      {tooltipContent !== null && (
        <Popper
          open={isOpen}
          anchorEl={containerRef.current}
          placement={placement}
          className={classNames(
            "scha__popper-tooltip",
            isNavigation && "scha__popper-tooltip__navigation",
            contentClassName,
          )}
          style={popperStyle}
          modifiers={
            disableFlip
              ? [
                  {
                    name: "flip",
                    options: {
                      fallbackPlacements: [],
                    },
                  },
                ]
              : undefined
          }
        >
          <div ref={tooltipRef}>
            <LCCard
              variant={cardVariant}
              disableHover={true}
              innerClassName={classNames("scha__popper-tooltip__inner", innerClassName)}
              onMouseEnter={addHover("tooltip")}
              onMouseLeave={removeHover("tooltip")}
            >
              {tooltipContent}
            </LCCard>
          </div>
        </Popper>
      )}
    </>
  );
});
